import type { AppState, DailyNote, Member, TaskInput, TaskRecord } from "../types";

const TOKEN_KEY = "osouji-access-token";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function getStoredToken(): string | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const sharedToken = hash.get("key");
  if (sharedToken) {
    localStorage.setItem(TOKEN_KEY, sharedToken);
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    return sharedToken;
  }
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function createShareUrl(token: string): string {
  return `${window.location.origin}${window.location.pathname}#key=${encodeURIComponent(token)}`;
}

async function request<T>(path: string, options: RequestInit = {}, token?: string | null): Promise<T> {
  const response = await fetch(`/api/${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string } & T;
  if (!response.ok) throw new ApiError(payload.error || "通信に失敗しました。", response.status);
  return payload;
}

export const api = {
  status: () => request<{ initialized: boolean }>("status"),

  bootstrap: (token: string, memberNames: string[]) =>
    request<{ ok: boolean }>(
      "bootstrap",
      { method: "POST", body: JSON.stringify({ memberNames }) },
      token,
    ),

  state: (token: string, from: string, to: string) =>
    request<AppState>(`state?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, {}, token),

  updateMembers: (token: string, members: Pick<Member, "id" | "name">[]) =>
    request<{ ok: boolean }>(
      "members",
      { method: "PATCH", body: JSON.stringify({ members }) },
      token,
    ),

  createTask: (token: string, input: TaskInput) =>
    request<{ id: string }>(
      "tasks",
      {
        method: "POST",
        body: JSON.stringify({
          name: input.name,
          scheduleType: input.scheduleType,
          scheduleValue: input.scheduleValue,
        }),
      },
      token,
    ),

  updateTask: (token: string, taskId: string, input: TaskInput) =>
    request<{ ok: boolean }>(
      `tasks/${taskId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          name: input.name,
          scheduleType: input.scheduleType,
          scheduleValue: input.scheduleValue,
        }),
      },
      token,
    ),

  deleteTask: (token: string, taskId: string) =>
    request<{ ok: boolean }>(`tasks/${taskId}`, { method: "DELETE" }, token),

  reorderTasks: (token: string, taskIds: string[]) =>
    request<{ ok: boolean }>(
      "tasks/reorder",
      { method: "PUT", body: JSON.stringify({ taskIds }) },
      token,
    ),

  updateRecord: (token: string, date: string, taskId: string, completed: boolean, memberId: string) =>
    request<{ record: TaskRecord | null }>(
      `records/${date}/${taskId}`,
      { method: "PUT", body: JSON.stringify({ completed, memberId }) },
      token,
    ),

  updateNote: (token: string, date: string, content: string, memberId: string) =>
    request<{ note: DailyNote | null }>(
      `notes/${date}`,
      { method: "PUT", body: JSON.stringify({ content, memberId }) },
      token,
    ),

  rotateToken: (token: string) => request<{ token: string }>("rotate-token", { method: "POST" }, token),

  exportUrl: () => "/api/export",
};

export async function downloadExport(token: string): Promise<void> {
  const response = await fetch(api.exportUrl(), { headers: { authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(payload.error || "バックアップを作成できませんでした。", response.status);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `osouji-backup-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
